'use client'

import { useTranslations } from 'next-intl'
import { cx } from 'class-variance-authority'
import { UserPlusIcon } from '@phosphor-icons/react/dist/ssr/UserPlus'
import { SignatureIcon } from '@phosphor-icons/react/dist/ssr/Signature'
import { ArrowFatLinesDownIcon } from '@phosphor-icons/react/dist/ssr/ArrowFatLinesDown'
import { ArrowFatLinesRightIcon } from '@phosphor-icons/react/dist/ssr/ArrowFatLinesRight'
import { FilePdfIcon } from '@phosphor-icons/react/dist/ssr/FilePdf'
import { FilePlusIcon } from '@phosphor-icons/react/dist/ssr/FilePlus'
import { XIcon } from '@phosphor-icons/react/dist/ssr/X'
import { Alert, Input, Select, Switch, Toast } from '@skop-app/lib-front'
import { currencies, vatValues } from '@skop-app/lib-domain/constants'
import type {
  Customer,
  DocumentMetadata,
  LabelType,
} from '@skop-app/lib-domain/types'

import { useUserContext } from '@/context'
import { openModal } from '@/components/modals/modal.registry.events'
import {
  canManageOrganizationSettings,
  canMutateDomain,
} from '@/features/permissions'
import { useCustomersByOrganization } from '@/services'
import {
  DocumensoSignDocument,
  OrderModalValues,
  SignDocumentTabOptions,
} from '@/types'
import { UserCard } from '@/components/atoms'
import { ResponsableSelect } from '@/components/molecules/ResponsableSelect/responsableSelect.molecule'
import { useSelectedTeamMembers } from '@/hooks/useSelectedTeamMembers'
import { useOrganizationMetadata } from '@/services/queries'

export const SignDocumentsOptions = ({
  order,
  effectiveVatPercentage,
  isLoading,
  twoSidesSign,
  signRequest,
  signDocument,
  setTwoSidesSign,
  setOrderValues,
  setSignDocument,
  options,
  showSignDocument,
  setShowSignDocument,
  className,
}: {
  order: OrderModalValues
  // Presentation only: setters must keep the editable tax choice from order.
  effectiveVatPercentage?: OrderModalValues['vatPercentage']
  twoSidesSign: boolean
  signRequest: DocumensoSignDocument
  setTwoSidesSign: React.Dispatch<React.SetStateAction<boolean>>
  isLoading: boolean
  setOrderValues: React.Dispatch<React.SetStateAction<OrderModalValues>>
  signDocument: LabelType | null
  setSignDocument: React.Dispatch<
    React.SetStateAction<{ label: string; value: string } | null>
  >
  options: SignDocumentTabOptions
  showSignDocument: boolean
  setShowSignDocument: React.Dispatch<React.SetStateAction<boolean>>
  className?: string
}) => {
  const t = useTranslations()
  const { team, authorization } = useUserContext()
  const { data: teamMembers = [] } = useSelectedTeamMembers(team?.id)
  const { data: metadata = [] } = useOrganizationMetadata(team?.id, [
    'DOCUMENT',
  ])
  const { data: customersData } = useCustomersByOrganization(team?.id)
  const isOrder = order?.type !== 'FAVORITE' && !options?.isTransferVoucher
  const requestType = order?.requestType
  const canManageDocuments = canManageOrganizationSettings(authorization)
  const canManageOrders = canMutateDomain(authorization, 'orders', 'canUpdate')

  const handleCreateDocument = () => {
    if (!canManageDocuments) {
      Toast.error(t('Documents-directory.permission-denied'))
      return
    }

    openModal('documentUpload', { showAllFilesButton: true })
  }

  const addCustomer = () => {
    if (!canManageOrders) {
      Toast.error(t('Customers-directory.permission-denied'))
      return
    }

    openModal('customerUpload', {})
  }

  const manager = teamMembers.find(
    (teammate) => teammate.userID === order?.managerID,
  )
  const documents = metadata as DocumentMetadata[]

  return (
    <div
      className={cx(
        'flex flex-col gap-4 rounded-lg bg-white p-4 shadow-md h-full border border-gray-200',
        className,
      )}
    >
      <ResponsableSelect
        order={order}
        setOrderValues={setOrderValues}
        isLoading={isLoading}
      />

      {!options?.hideOptionsAllItems && (
        <>
          {!options?.hideSendSignButton && (
            <div className="flex flex-col gap-4">
              <Alert.Root variant="info">
                <Alert.Description>
                  {t('Documents-directory.sign_parties')}
                </Alert.Description>
              </Alert.Root>
              <div className="flex w-full flex-col items-center justify-center lg:flex-row">
                <UserCard
                  name={manager?.name ?? order?.shop?.user?.name ?? ''}
                  description={manager?.email ?? order?.shop?.user?.email ?? ''}
                  className="h-64 w-60"
                >
                  <p>{isOrder ? 'Vendeur' : 'Cédant'}</p>
                  <div
                    title={
                      twoSidesSign ? 'Doit signer' : "N'a pas besoin de signer"
                    }
                  >
                    <SignatureIcon
                      className={cx({
                        'size-8': true,
                        'text-blue-500': twoSidesSign,
                        'text-gray-500': !twoSidesSign,
                        'text-green-500':
                          twoSidesSign && signRequest?.recipients[0].signedAt,
                      })}
                    />
                  </div>
                </UserCard>
                <ArrowFatLinesRightIcon className="mx-12 hidden size-12 text-slate-300 lg:block" />
                <ArrowFatLinesDownIcon className="my-12 block size-12 text-slate-300 lg:hidden" />
                <UserCard
                  name={order?.name ?? ''}
                  description={order?.email ?? ''}
                  className="z-10 h-64 w-60"
                >
                  <div className="flex flex-col items-center justify-center gap-2">
                    {!options?.hideClient && !order?.name && !order?.email && (
                      <Select
                        className="w-fit min-w-48 max-w-52"
                        key="customer"
                        placeholder={t('Customers-directory.add-customer')}
                        isDisabled={isLoading}
                        value={order?.customer}
                        onChange={(value: Customer) =>
                          setOrderValues({
                            ...order,
                            name: value.lastName + ' ' + value.firstName,
                            email: value.email || '',
                            phone: value.phone,
                            customerID: value.id,
                            customer: {
                              ...value,
                              label: value.lastName + ' ' + value.firstName,
                              value: value.id,
                            },
                            billingAddress: {
                              name: value.lastName + ' ' + value.firstName,
                              email: value.email,
                              phone: value.phone,
                              address: value.address,
                              city: value.city,
                              zipcode: value.zipcode,
                              country: value.country,
                            },
                            deliveryAddress: {
                              name: value.lastName + ' ' + value.firstName,
                              email: value.email,
                              phone: value.phone,
                              address: value.address,
                              city: value.city,
                              zipcode: value.zipcode,
                              country: value.country,
                            },
                          })
                        }
                        options={[
                          ...(customersData ?? []).map(
                            (customer: Customer) => ({
                              ...customer,
                              label:
                                customer.lastName + ' ' + customer.firstName,
                              value: customer.id,
                            }),
                          ),
                          ...(canManageOrders
                            ? [
                                {
                                  label: t('Customers-directory.add-customer'),
                                  value: 'addClient',
                                  type: 'custom' as const,
                                  onClick: addCustomer,
                                  icon: <UserPlusIcon className="size-4" />,
                                  className: 'hover:!bg-primary-300',
                                },
                              ]
                            : []),
                        ]}
                        closeMenuOnSelect={true}
                      />
                    )}
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <p>{isOrder ? 'Acheteur' : 'Cessionnaire'}</p>
                        <div title="Doit signer">
                          <SignatureIcon
                            className={cx({
                              'size-8 text-blue-500': true,
                              'text-green-500':
                                (!twoSidesSign &&
                                  signRequest?.recipients[0]?.signedAt) ||
                                (twoSidesSign &&
                                  signRequest?.recipients[1]?.signedAt),
                            })}
                          />
                        </div>
                      </div>
                      {!options?.hideClient &&
                        !!order?.name &&
                        !!order?.email && (
                          <XIcon
                            className="size-6 cursor-pointer text-red-500"
                            onClick={() =>
                              setOrderValues({
                                ...order,
                                name: '',
                                email: '',
                                customer: null,
                              })
                            }
                          />
                        )}
                    </div>
                  </div>
                </UserCard>
              </div>

              {(requestType === 'information' ||
                options?.activateTwoSidesSign) && (
                <>
                  <Alert.Root variant="info">
                    <Alert.Description>
                      Activer cette option pour permettre de signer le document
                      par les deux parties (vous et{' '}
                      {isOrder ? "l'acheteur" : 'le cessionnaire'}).
                    </Alert.Description>
                  </Alert.Root>

                  <div className="mx-2 flex gap-4">
                    <Switch
                      id="documentDefault"
                      className="mt-1"
                      title={
                        twoSidesSign
                          ? t('Common.deactivate')
                          : t('Common.activate')
                      }
                      defaultChecked={twoSidesSign}
                      onCheckedChange={(value: boolean) =>
                        setTwoSidesSign(value)
                      }
                    />
                    <div>
                      {t('Documents-directory.co_sign')}
                      <p className="text-sm">
                        {twoSidesSign
                          ? `Vous et ${isOrder ? "l'acheteur" : 'le cessionnaire'} devrez signer ce document`
                          : `Seul ${isOrder ? "l'acheteur" : 'le cessionnaire'} devra signer ce document`}
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
          <Alert.Root variant="info">
            <Alert.Description>
              {t('Documents-directory.sign_document')}
            </Alert.Description>
          </Alert.Root>
          <div className="flex flex-col gap-4 md:flex-row">
            <div
              className={cx(
                'flex w-full flex-col gap-1',
                order?.type === 'CART' && 'lg:w-1/2',
              )}
            >
              <label htmlFor="issueDate" className="text-sm font-normal">
                {t('Documents-directory.document-issue-date')}
              </label>
              <Input
                name="issueDate"
                type="datetime-local"
                className="w-full"
                defaultValue={
                  order.issueDate
                    ? new Date(order.issueDate)
                        .toLocaleString('sv-SE', { timeZone: 'Europe/Paris' })
                        .slice(0, 16)
                        .replace(' ', 'T')
                    : new Date(new Date().setDate(new Date().getDate()))
                        .toLocaleString('sv-SE', { timeZone: 'Europe/Paris' })
                        .slice(0, 16)
                        .replace(' ', 'T')
                }
                onChange={(e) => {
                  setOrderValues({
                    ...order,
                    issueDate: new Date(
                      e.target.value || new Date(),
                    ).toISOString(),
                  })
                }}
              />
            </div>
            {order?.type !== 'CART' && (
              <div className="flex w-full flex-col gap-1">
                <label htmlFor="expireDate" className="text-sm font-normal">
                  {t('Documents-directory.document-expire-date')}
                </label>
                <Input
                  name="expireDate"
                  type="datetime-local"
                  className="w-full"
                  defaultValue={
                    order.expireDate
                      ? new Date(order.expireDate)
                          .toLocaleString('sv-SE', { timeZone: 'Europe/Paris' })
                          .slice(0, 16)
                          .replace(' ', 'T')
                      : new Date(new Date().setDate(new Date().getDate() + 30))
                          .toLocaleString('sv-SE', { timeZone: 'Europe/Paris' })
                          .slice(0, 16)
                          .replace(' ', 'T')
                  }
                  onChange={(e) => {
                    setOrderValues({
                      ...order,
                      expireDate: new Date(
                        e.target.value || new Date(),
                      ).toISOString(),
                    })
                  }}
                />
              </div>
            )}
          </div>
          {!!(signDocument || isOrder) && (
            <div className="grid grid-cols-2 gap-4">
              {!!signDocument && (
                <div className="col-span-2 flex items-center gap-2 rounded bg-zinc-100 p-4 lg:col-span-1">
                  <Switch
                    id="hideDefaultDocument"
                    defaultChecked={showSignDocument}
                    onCheckedChange={(value: boolean) =>
                      setShowSignDocument(value)
                    }
                  />
                  <label
                    htmlFor="hideDefaultDocument"
                    className="text-sm font-normal"
                  >
                    {t('Order.hideDefaultDocument', {
                      isOrderDefaultDocument: showSignDocument
                        ? 'true'
                        : 'false',
                    })}
                  </label>
                </div>
              )}
              {!!isOrder && (
                <>
                  <div className="col-span-2 flex items-center gap-2 rounded bg-zinc-100 p-4 lg:col-span-1">
                    <Switch
                      id="conditions"
                      defaultChecked={order?.orderParameters?.showOnInvoices}
                      onCheckedChange={(value: boolean) => {
                        setOrderValues({
                          ...order,
                          orderParameters: {
                            ...order.orderParameters,
                            showOnInvoices: value,
                          },
                        })
                      }}
                    />
                    <label
                      htmlFor="conditions"
                      className="line-clamp-1 text-sm font-normal"
                    >
                      {t('Documents-directory.showBankInformationsLabel', {
                        isOrderDefaultDocument: order?.orderParameters
                          ?.showOnInvoices
                          ? 'true'
                          : 'false',
                      })}
                    </label>
                  </div>
                  {(order?.requestType === 'quote' ||
                    order?.type === 'FAVORITE') && (
                    <div className="col-span-2 flex items-center gap-2 rounded bg-zinc-100 p-4 lg:col-span-1">
                      <Switch
                        id="signInputs"
                        defaultChecked={!order?.orderParameters?.hideSignInputs}
                        onCheckedChange={(value: boolean) => {
                          setOrderValues({
                            ...order,
                            orderParameters: {
                              ...order.orderParameters,
                              hideSignInputs: !value,
                            },
                          })
                        }}
                      />
                      <label
                        htmlFor="signInputs"
                        className="line-clamp-1 text-sm font-normal"
                      >
                        {t('Documents-directory.hideSignInputsLabel', {
                          isOrderDefaultDocument: order?.orderParameters
                            ?.hideSignInputs
                            ? 'true'
                            : 'false',
                        })}
                      </label>
                    </div>
                  )}
                  {order?.type !== 'FAVORITE' &&
                    !options?.hideSendSignButton &&
                    order.requestType === 'quote' && (
                      <div className="col-span-2 flex items-center gap-2 rounded bg-zinc-100 p-4 lg:col-span-1">
                        <Switch
                          id="showSaleConditions"
                          defaultChecked={
                            order?.orderParameters?.showSaleConditions
                          }
                          onCheckedChange={(value: boolean) => {
                            setOrderValues({
                              ...order,
                              orderParameters: {
                                ...order.orderParameters,
                                showSaleConditions: value,
                              },
                            })
                          }}
                        />
                        <label
                          htmlFor="showSaleConditions"
                          className="line-clamp-1 text-sm font-normal"
                        >
                          {t('Documents-directory.showSaleConditionsLabel', {
                            isSaleConditions: order?.orderParameters
                              ?.showSaleConditions
                              ? 'true'
                              : 'false',
                          })}
                        </label>
                      </div>
                    )}
                </>
              )}
            </div>
          )}
          {!options?.hideVatAndCurrency && (
            <>
              <Alert.Root variant="info">
                <Alert.Description>
                  {t('Documents-directory.vatAndCurrencyDescription')}
                </Alert.Description>
              </Alert.Root>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="vatPercentage"
                    className="text-sm font-normal"
                  >
                    {t('Account.vat')} (%)
                  </label>
                  <Select
                    id="vatPercentage"
                    className="w-full min-w-0"
                    placeholder="20"
                    isDisabled={isLoading}
                    value={
                      effectiveVatPercentage ?? order?.vatPercentage ?? null
                    }
                    onChange={(value: LabelType & { value?: number }) => {
                      if (value && typeof value.value === 'number') {
                        setOrderValues({
                          ...order,
                          vatPercentage: {
                            label: value.label,
                            value: value.value,
                          },
                        })
                      } else {
                        setOrderValues({
                          ...order,
                          vatPercentage: undefined,
                        })
                      }
                    }}
                    options={vatValues}
                    overflow
                    closeMenuOnSelect
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label htmlFor="currency" className="text-sm font-normal">
                    {t('Account.currency')}
                  </label>
                  <Select
                    id="currency"
                    className="w-full min-w-0"
                    placeholder="EUR"
                    isDisabled={isLoading}
                    value={order?.currency ?? null}
                    onChange={(value: LabelType & { sign?: string }) =>
                      setOrderValues((prev) => ({
                        ...prev,
                        currency: value ?? undefined,
                      }))
                    }
                    options={currencies.map((currencyItem) => ({
                      ...currencyItem,
                      label:
                        currencyItem.label + ' (' + currencyItem.sign + ')',
                      value: currencyItem.value,
                    }))}
                    overflow
                    closeMenuOnSelect
                  />
                </div>
              </div>
            </>
          )}
          {!options?.hideSelectSignDocument && (
            <>
              <Alert.Root variant="info">
                <Alert.Description>
                  {t('Documents-directory.add_document_description', {
                    documentType: isOrder
                      ? t('Documents-directory.quote').charAt(0).toLowerCase() +
                        t('Documents-directory.quote').slice(1)
                      : t('Documents-directory.transfer_voucher')
                          .charAt(0)
                          .toLowerCase() +
                        t('Documents-directory.transfer_voucher').slice(1),
                  })}
                </Alert.Description>
              </Alert.Root>
              <div className="mx-2 flex items-start gap-4">
                <div className="mt-2 flex w-12 items-start justify-center">
                  <FilePdfIcon className="size-6 text-black" />
                </div>
                <div className=" w-full">
                  <Select
                    className="w-fit min-w-64 lg:min-w-56"
                    key="signDocument"
                    placeholder={t('Documents-directory.select_document')}
                    isDisabled={isLoading}
                    value={signDocument}
                    onChange={(value: { label: string; value: string }) =>
                      setSignDocument(value)
                    }
                    options={[
                      ...documents.map((doc) => ({
                        label: doc.value.name,
                        value: doc.value.link,
                      })),
                      ...(canManageDocuments
                        ? [
                            {
                              label: t('Documents-directory.add_document'),
                              value: 'addDocument',
                              type: 'custom' as const,
                              onClick: handleCreateDocument,
                              icon: <FilePlusIcon className="size-4" />,
                              className: 'hover:!bg-primary-300',
                            },
                          ]
                        : []),
                    ]}
                    isSearchable={false}
                    isClearable
                    overflow={true}
                    closeMenuOnSelect={true}
                  />
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  )
}
